import React, {
  Dispatch, ReactElement, SetStateAction, useEffect, useState,
} from 'react';
import {
  ActivityIndicator, Animated, AppState, Image, Pressable, ScrollView, StyleSheet, Text, View,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import * as Animatable from 'react-native-animatable';
import QRCode from 'react-native-qrcode-svg';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Moment from 'moment';
import Modal from 'react-native-modal';
import * as Clipboard from 'expo-clipboard';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import DeleteIcon24 from '../24pxIcons/DeleteIcon24';
import DownArrow24 from '../24pxIcons/DownArrow24';
import ZoomIcon from '../24pxIcons/ZoomIcon';
import AddEditNote from '../AddEditNote';
import EditIcon24 from '../24pxIcons/EditIcon24';
import OrionArrowIcon from '../24pxIcons/OrionArrowIcon';
import EditAssetModal from './EditAssetModal';
import getAssetTickets from '../../requests/Assets/GetAssetTickets';
import AlertModal from '../AlertModal';
import retireAsset from '../../requests/Assets/RetireAsset';
import ReportIssueIcon from '../12pxIcons/ReportIssueIcon';
import ReportTicketModal from './ReportTicketModal';
import CopyIcon from '../24pxIcons/CopyIcon';
import getSolveTags from '../../requests/Assets/GetSolveTags';
import deleteSolveTag from '../../requests/Assets/DeleteSolveTag';
import { logEvent } from '../LogEvent';
import Style from '../../constants/Style';
import LocationIcon from '../30pxIcons/LocationIcon';
import {
  AssetInstance, AssetNote, AssetCustomField, Template, TemplateType,
  UserPermission,
} from '../../constants/Types';
import PressableStyled, { PressableState } from '../PressableStyled';
import { loadCustomFields } from './data';
import {
  ADDITIONAL_FIELD_TYPES,
} from './AddTemplateModal/components/FieldList/components/FieldItem';
import {
  ADD_ASSET_FAILURE_MESSAGE, COPIED_TO_CLIPBOARD, DELETE_ASSET_SUCCESS, DELETE_SOLVE_TAG_SUCCESS,
  PPM_URL_MISSING,
} from '../../constants/AlertMessages';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import { openInNewTab } from '../../constants/Utils';
import {
  updateTicketsFilterAssetId, updateTicketsFilterAssetName,
} from '../../redux/ticketsFilterSlice';

const placeholder = require('../../assets/images/placeholder.png');

interface AssetRowProps {
  cardOpen: boolean,
  asset: AssetInstance,
  deleteAsset,
  updateAsset,
  showAlert,
  setLoading: Dispatch<SetStateAction<boolean>>,
  buildingId: string,
  selected: boolean,
  template: Template,
}

export default function AssetRow(props: AssetRowProps) {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const dispatch = useDispatch();
  const [cardOpen, setCardOpen] = useState<boolean>(props.cardOpen);
  const [switchToggled] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [qrCodeSelected, setQrCodeSelected] = useState<boolean>(true);
  const [domain, setDomain] = useState<string>('https://hereworks.tech/');
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [addNoteModalVisible, setAddNoteModalVisible] = useState<boolean>(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [editNote, setEditNote] = useState<boolean>(false);
  const [daySelected, setDaySelected] = useState<boolean>(true);
  const [weekSelected, setWeekSelected] = useState<boolean>(false);
  const [monthSelected, setMonthSelected] = useState<boolean>(false);
  const [yearSelected, setYearSelected] = useState<boolean>(false);
  const [editAssetVisible, setEditAssetVisible] = useState<boolean>(false);
  const [ticketCounts, setTicketCounts] = useState<[number, number, number, number]>([0, 0, 0, 0]);
  const [selectedFilterIndex, setSelectedFilterIndex] = useState<number>(0);
  const [id] = useState<string>(props.asset.id);
  const [image, updateImage] = useState<string>(
    props.asset.imageUrl?.length > 0 ? `${props.asset.imageUrl}?time=${Date.now()}` : null,
  );
  const [notes, setNotes] = useState<AssetNote[]>(props.asset.notes ?? []);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertError, setAlertError] = useState(true);
  const [alertIsMessage, setAlertIsMessage] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertAction, setAlertAction] = useState(null);
  const [alertActionTitle, setAlertActionTitle] = useState('');
  const [reportIssueModalVisible, setReportIssueModalVisible] = useState(false);
  const [currentTagIndex, setCurrentTag] = useState(0);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [solveTags, setSolveTags] = useState([{
    solveTagId: props.asset.id,
  }]);
  const canEditAsset: boolean = isAdmin || permissions.includes(UserPermission.ASSETS_UPDATE);
  const canDeleteAsset: boolean = isAdmin || permissions.includes(UserPermission.ASSETS_DELETE);
  const canReportTicket: boolean = isAdmin || permissions.includes(UserPermission.TICKETS_CREATE);

  const customFields: AssetCustomField[] = loadCustomFields(props.template, props.asset, false);

  const showAlert = (
    title: string,
    message?: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = null,
    isMessage: boolean = false,
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertVisible(true);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setAlertIsMessage(isMessage);
  };

  const navigation = useNavigation();

  useEffect(() => {
    AsyncStorage.getItem('environment').then((result) => {
      if (result === 'dev') setDomain('https://dev.hereworks.tech/');
      else if (result === 'demo') setDomain('https://demo.hereworks.tech/');
    });
    const listener = AppState.addEventListener('change', loadTicketCount);
    return () => listener.remove();
  }, []);

  const loadTicketCount = () => {
    getAssetTickets(props.asset.id, props.buildingId)
      .then((response) => {
        const values = response?.Tickets;
        setTicketCounts([
          values?.day ?? 0,
          values?.week ?? 0,
          values?.month ?? 0,
          values?.year ?? 0,
        ]);
      });
    getSolveTags(props.asset.id).then((response) => {
      const tags = response?.solveTags ?? [];
      if (tags.length > 0) {
        setSolveTags(response?.solveTags);
      }
      setTagsLoading(false);
    });
  };

  const getMapButtonText = () => {
    if (props.asset.customFields.some((field) => field.name === 'GeoX' && Number(field.value) > 0)) {
      return 'View on map';
    }
    if (isAdmin || permissions.includes(UserPermission.TICKETS_UPDATE)) {
      return 'Set location';
    }
    return 'No location';
  };

  useEffect(() => {
    if (cardOpen) loadTicketCount();
  }, [cardOpen]);

  const deleteError = (errorText?: string) => showAlert(
    'Error',
    errorText ?? ADD_ASSET_FAILURE_MESSAGE,
  );

  const deleteAsset: () => void = () => {
    showAlert(
      'Delete the asset?',
      <Text>
        {'Do you want to delete the asset - '}
        <Text style={Style.alertBoldText}>
          {props.asset.name}
        </Text>
        {'?'}
      </Text>,
      true,
      () => {
        logEvent('AssetRow', 'delete_asset');
        props.setLoading(true);
        retireAsset(props.asset.id)
          .then((response) => {
            props.setLoading(false);
            if (response && !response?.isError) {
              props.deleteAsset(id);
              props.showAlert('Success', DELETE_ASSET_SUCCESS, false);
            } else deleteError(response?.errorText);
          }).catch((error) => {
            deleteError(error?.errorText);
            props.setLoading(false);
          });
      },
      'Delete',
    );
  };

  const rowDivider = () => (
    <View
      style={{
        backgroundColor: Colors.light.hereworksWhite500,
        width: 1,
        height: 30,
      }}
    />
  );

  const renderCardClosedView = () => (
    <Animatable.View animation={'fadeIn'} duration={300} style={{ flex: 1 }}>
      <PressableStyled
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          flex: 1,
        }}
        onPress={() => {
          logEvent('AssetRow', 'Open_card');
          setCardOpen(!cardOpen);
        }}
      >
        <View
          style={{
            width: 8,
            height: 60,
            zIndex: 1,
            backgroundColor: switchToggled
              ? Colors.light.hereworksGreen400
              : Colors.light.hereworksWhite600,
          }}
        />
        <View
          style={{
            flexDirection: 'row',
            flex: 1,
            marginRight: 96,
          }}
        >
          <View style={{ flex: 2, alignItems: 'flex-start' }}>
            <Text selectable style={styles.rowText}>
              {props.asset.name?.length > 0 ? props.asset.name : '-'}
            </Text>
          </View>
          {rowDivider()}
          <View style={{ flex: 2, alignItems: 'flex-start' }}>
            <Text selectable style={styles.rowText}>
              {props.asset.locationFloor?.length > 0 ? props.asset.locationFloor : '-'}
            </Text>
          </View>
          {rowDivider()}
          <View style={{ flex: 2, alignItems: 'flex-start' }}>
            <Text selectable style={styles.rowText}>
              {props.asset.locationArea?.length > 0 ? props.asset.locationArea : '-'}
            </Text>
          </View>
        </View>
      </PressableStyled>
    </Animatable.View>
  );

  const renderLocation = () => <View style={styles.labelSetQuarterSize}>
    <Pressable
      style={(state) => [{
        flexDirection: 'row',
        marginRight: 10,
        borderRadius: 8,
        backgroundColor: state.hovered ? Colors.light.hereworksBlue50 : '#fff',
        alignSelf: 'flex-start',
      }]}
      onPress={() => {
        logEvent('AssetRow', 'view_to_map');
        const onMap = props.asset?.customFields.some(
          (field) => field.name === 'GeoX' && Number(field.value) > 0,
        );
        if (!onMap && !(isAdmin || permissions.includes(UserPermission.TICKETS_UPDATE))) {
          showAlert('Location not set', 'This asset is not placed on the map.');
          return;
        }
        navigation.navigate(AppScreen.MAP as any, {
          floor: props.asset?.locationFloor,
          area: props.asset?.locationArea,
          space: props.asset?.locationSpace,
          locationId: props.asset?.locationId,
          assetsView: props.asset.type === TemplateType.ASSET,
          sensorsView: props.asset.type === TemplateType.SENSOR
            || props.asset.type === TemplateType.METER,
          assetId: props.asset?.id,
          placeAsset: !onMap && (isAdmin || permissions.includes(UserPermission.TICKETS_UPDATE)),
        });
      }}>
      <View style={{ justifyContent: 'center' }}>
        <Text selectable style={styles.viewMapText}>
          {getMapButtonText()}
        </Text>
      </View>
      <LocationIcon color={Colors.light.hereworksBlue500} />
    </Pressable>
  </View>;

  const renderMainView = () => (
    <View
      style={styles.mainInformationContainer}
      testID='asset-main-modal'
    >
      <Modal isVisible={modalVisible} animationIn={'fadeIn'} animationOut={'fadeOut'}>
        <PressableStyled
          onPress={() => {
            logEvent('AssetRow', 'Close_image_modal');
            setModalVisible(!modalVisible);
          }}
          style={{ height: '100%', flex: 1 }}
        >
          <Image
            source={{ uri: image }}
            resizeMode={'center'}
            style={{ width: undefined, height: '100%' }}
          />
        </PressableStyled>
      </Modal>
      <View>
        <Pressable
          style={{ marginBottom: 12 }}
          onPress={() => {
            logEvent('AssetRow', 'Open_image_modal');
            setModalVisible(true);
          }}
          disabled={!image}
        >
          {({ hovered }: PressableState): ReactElement => (
            <View style={{ opacity: hovered ? 0.5 : 1 }}>
              <Image
                source={{ uri: image }}
                resizeMode={'cover'}
                style={{ width: 108, height: 108 }}
                defaultSource={placeholder}
              />
              {!!image && <ZoomIcon
                style={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  borderRadius: 3,
                  backgroundColor: hovered ? Colors.light.hereworksBlue500
                    : `${Colors.light.hereworksBlack300}05`,
                }}
              />}
            </View>
          )}
        </Pressable>
      </View>
      <View style={styles.labelsContainer}>
        <View style={styles.labelSetHalfSize}>
          <Text style={styles.mainDescText}>{'Description'}</Text>
          <Text selectable style={styles.mainValueText}>
            {props.asset.description?.length > 0 ? props.asset.description : '-'}
          </Text>
        </View>
        <View style={styles.labelSetQuarterSize}>
          <View style={{ flexDirection: 'row' }}>
            <View>
              <Text style={styles.mainDescText}>{'Floor'}</Text>
              <Text selectable style={styles.mainValueText}>
                {props.asset.locationFloor?.length > 0 ? props.asset.locationFloor : '-'}
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.labelSetQuarterSize}>
          <Text style={styles.mainDescText}>{'Model'}</Text>
          <Text selectable style={styles.mainValueText}>
            {props.asset.model?.length ? props.asset.model : '-'}
          </Text>
        </View>
        <View style={styles.labelSetHalfSize}>
          <Text style={styles.mainDescText}>{'Asset ID'}</Text>
          <Text selectable style={styles.mainValueText}>
            {id?.length > 0 ? id : '-'}
          </Text>
        </View>
        {props.asset.locationArea ? <View style={styles.labelSetQuarterSize}>
          <Text style={styles.mainDescText}>{'Area'}</Text>
          <Text selectable style={styles.mainValueText}>
            {props.asset.locationArea?.length > 0 ? props.asset.locationArea : '-'}
          </Text>
        </View> : <></>}
        <View style={styles.labelSetQuarterSize}>
          <Text style={styles.mainDescText}>{'Serial number'}</Text>
          <Text selectable style={styles.mainValueText}>
            {props.asset.serial?.length > 0 ? props.asset.serial : '-'}
          </Text>
        </View>
        <View style={styles.labelSetHalfSize}>
          <Text style={styles.mainDescText}>{'Template'}</Text>
          <Text selectable style={styles.mainValueText}>
            {props.template?.name?.length > 0 ? props.template.name : '-'}
          </Text>
        </View>
        {props.asset.locationSpace ? <View style={styles.labelSetQuarterSize}>
          <Text style={styles.mainDescText}>{'Space'}</Text>
          <Text selectable style={styles.mainValueText}>
            {props.asset.locationSpace?.length > 0 ? props.asset.locationSpace : '-'}
          </Text>
        </View> : <></>}
        {renderLocation()}
        <View style={styles.labelSetQuarterSize}>
          <Text style={styles.mainDescText}>{'Manufacturer'}</Text>
          <Text selectable style={styles.mainValueText}>
            {props.asset.manufacturer?.length > 0 ? props.asset.manufacturer : '-'}
          </Text>
        </View>
      </View>
    </View>
  );

  const renderNotes = () => (
    <View
      style={styles.notesSection}
      testID='note-section'
    >
      <AddEditNote
        modalVisible={addNoteModalVisible}
        {...{
          notes, selectedNote, editNote, setNotes,
        }}
        setVisible={() => setAddNoteModalVisible(false)}
        setEditNote={() => setEditNote(true)}
        assetId={props.asset.id}
      />
      <View style={{ height: 180 }}>
        <ScrollView
          style={{
            flex: 1,
            marginTop: 16,
            width: '100%',
          }}
        >
          {notes.map((note, index: number) => (
            <PressableStyled
              key={`asset-note#~${index + 1}`}
              testID={`asset-note#~${index + 1}`}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginVertical: 4,
                marginTop: 8,
                borderColor: Colors.light.hereworksWhite500,
                borderTopWidth: index > 0 ? 1 : 0,
              }}
              onPress={() => {
                logEvent('AssetRow', 'Edit_note');
                setSelectedNote(note);
                setEditNote(false);
                setAddNoteModalVisible(!addNoteModalVisible);
              }}
            >
              <View
                style={{
                  backgroundColor: Colors.light.hereworksDarkBlue,
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  marginRight: 8,
                }}
              />
              <View style={{ flex: 1, marginTop: 8 }}>
                <View style={{ flexDirection: 'row', flex: 1 }}>
                  <Text selectable style={styles.noteTitleText} numberOfLines={1}>
                    {note.title}
                  </Text>
                  <View style={{ flex: 1, alignItems: 'flex-end' }}>
                    <Text selectable style={styles.noteDateText}>
                      {Moment(note.updatedOn).format('DD.MM.YYYY')}
                    </Text>
                  </View>
                </View>
                <Text selectable style={styles.noteNameText}>{`by ${note.name}`}</Text>
              </View>
            </PressableStyled>
          ))}
        </ScrollView>
      </View>
      {canEditAsset && <PressableStyled
        style={styles.addNoteButton}
        onPress={() => {
          logEvent('AssetRow', 'Add_note');
          setEditNote(false);
          setSelectedNote(null);
          setAddNoteModalVisible(!addNoteModalVisible);
        }}
      >
        <Text style={[styles.secondaryButtonText, { color: Colors.light.hereworksBlue500 }]}>
          {'Add note'}
        </Text>
      </PressableStyled>}
    </View>
  );

  const renderMainDetails = () => <View style={styles.assetHeaderButtonSection}>
    <Pressable
      style={({ hovered, pressed }: PressableState) => [
        pressed && Style.pressed,
        hovered && Style.hovered, {
          borderColor: Colors.light.hereworksBlue500,
          borderRadius: 4,
          borderWidth: 1,
          marginEnd: 8,
          paddingVertical: 2,
          backgroundColor: '#fff',
        },
      ]}
      onPress={() => {
        logEvent('AssetRow', 'ppm_button');
        const ppmLink = `${props.template?.ppmUrl}${props.asset.id}`;
        if (!props.template?.ppmUrl) showAlert('Error', PPM_URL_MISSING, true, null, null, true);
        else { openInNewTab(ppmLink); }
      }}
    >
      <Text style={{
        color: Colors.light.hereworksBlue500,
        fontFamily: 'Roboto_400Regular',
        fontSize: 12,
        padding: 8,
      }}>PPM</Text>
    </Pressable>
    {canReportTicket && <PressableStyled
      style={styles.detailedFilterButton}
      onPress={() => {
        logEvent('AssetRow', 'report_issue');
        setReportIssueModalVisible(true);
      }}
      testID='am-report-ticket-btn'
    >
      <ReportIssueIcon />
    </PressableStyled>}
    <View style={styles.assetHeaderButtonSectionDivider} />
    <PressableStyled
      style={[
        styles.secondaryButton,
        { backgroundColor: selectedTab === 0 ? Colors.light.hereworksBlue500 : '#fff' },
      ]}
      onPress={() => {
        logEvent('AssetRow', 'Main_tab');
        setSelectedTab(0);
      }}
      testID='main-tab-btn'
    >
      <Text
        style={[
          styles.secondaryButtonText,
          { color: selectedTab !== 0 ? Colors.light.hereworksBlue500 : '#fff' },
        ]}
      >
        {'MAIN'}
      </Text>
    </PressableStyled>
    <PressableStyled
      style={[
        styles.secondaryButton,
        {
          backgroundColor: selectedTab !== 0 ? Colors.light.hereworksBlue500 : '#fff',
          marginRight: 78,
        },
      ]}
      onPress={() => {
        logEvent('AssetRow', 'Details_tab');
        setSelectedTab(1);
      }}
    >
      <Text
        style={[
          styles.secondaryButtonText,
          { color: selectedTab === 0 ? Colors.light.hereworksBlue500 : '#fff' },
        ]}
      >
        {'DETAILS'}
      </Text>
    </PressableStyled>
  </View>;

  const currentTagId: string = solveTags[currentTagIndex]?.solveTagId;

  return (
    <Animated.View
      style={{
        marginBottom: 12,
        alignItems: 'center',
      }}
    >
      <View
        style={{
          backgroundColor: Colors.light.hereworksWhite100,
          borderRadius: 10,
          overflow: 'hidden',
          alignSelf: 'stretch',
          flex: 1,
        }}
      >
        {cardOpen ? (
          <Animatable.View animation={'fadeIn'} style={{ flexDirection: 'row', flex: 1 }}>
            <View
              style={{
                width: 17,
                height: '100%',
                zIndex: 1,
                backgroundColor:
                  props.selected || switchToggled
                    ? Colors.light.hereworksGreen400
                    : Colors.light.hereworksWhite600,
              }}
            />
            <View style={{ flex: 1 }}>
              <View style={styles.assetRowHeader}>
                <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.assetTitleSub}>Asset's Name</Text>
                    <Text selectable style={styles.assetTitle} numberOfLines={1}>
                      {props.asset.name}
                    </Text>
                  </View>
                  {canEditAsset && <Pressable
                    style={{ marginHorizontal: 8 }}
                    onPress={() => {
                      logEvent('AssetRow', 'Edit_asset');
                      setEditAssetVisible(!editAssetVisible);
                    }}
                    testID='am-edit-asset-btn'
                  >
                    {({ hovered }: PressableState): ReactElement => (
                      <View
                        style={{
                          backgroundColor: hovered
                            ? Colors.light.hereworksBlue500
                            : 'transparent',
                          borderRadius: 4,
                        }}
                      >
                        <EditIcon24 color={hovered ? '#fff' : null} />
                      </View>
                    )}
                  </Pressable>}
                </View>
                {renderMainDetails()}
              </View>
              {selectedTab === 1 && (
                <>
                  <View
                    style={{
                      flexDirection: 'row',
                      marginBottom: 12,
                    }}
                    testID='assets-details-modal'
                  >
                    <View
                      style={{
                        marginVertical: 16,
                        marginHorizontal: 24,
                        borderWidth: 1,
                        borderRadius: 12,
                        borderColor: Colors.light.hereworksBlue200,
                        backgroundColor: '#fff',
                        width: 461,
                      }}
                    >
                      <View style={{ flexDirection: 'row' }}>
                        <View
                          style={{
                            flex: 1,
                            margin: 12,
                            borderWidth: 1,
                            borderRadius: 12,
                            borderColor: Colors.light.hereworksBlue200,
                            flexDirection: 'row',
                            overflow: 'hidden',
                          }}
                        >
                          <View
                            style={{
                              paddingHorizontal: 19,
                              paddingVertical: 17,
                              borderRightWidth: 1,
                              borderColor: Colors.light.hereworksBlue200,
                              width: 124,
                            }}
                          >
                            {qrCodeSelected && currentTagId && (
                              <QRCode
                                value={`${`${domain}${currentTagId}`}?ss=QR`}
                                size={70}
                              />
                            )}
                            {!qrCodeSelected && <FontAwesome name={'wifi'} size={70} />}
                          </View>
                          <View style={{ flex: 1 }}>
                            <View style={{
                              marginVertical: 12, marginHorizontal: 12, flex: 1,
                            }}>
                              <Text selectable>{`ID: ${solveTags[currentTagIndex]?.solveTagId}`}</Text>
                              <View style={styles.tagSection}>
                                <PressableStyled
                                  onPress={() => {
                                    logEvent('AssetRow', 'previous_tag');
                                    if (currentTagIndex > 0) {
                                      setCurrentTag(currentTagIndex - 1);
                                    }
                                  }}
                                  style={{
                                    opacity: currentTagIndex > 0 ? 1 : 0.4,
                                  }}
                                  disabled={currentTagIndex === 0}
                                  testID='am-select-previous-asset-tag-button'
                                >
                                  <OrionArrowIcon />
                                </PressableStyled>
                                <Text style={styles.tagNumbers}>
                                  {`${currentTagIndex + 1}/${solveTags.length}`}
                                </Text>
                                <PressableStyled
                                  onPress={() => {
                                    logEvent('AssetRow', 'next_tag');
                                    setCurrentTag(currentTagIndex + 1);
                                  }}
                                  style={{
                                    opacity: currentTagIndex < solveTags.length - 1 ? 1 : 0.4,
                                  }}
                                  disabled={currentTagIndex === solveTags.length - 1}
                                  testID='am-select-next-asset-tag-button'
                                >
                                  <OrionArrowIcon style={{ transform: [{ rotateZ: '180deg' }] }} />
                                </PressableStyled>
                              </View>
                              {canEditAsset && solveTags?.length > 1 && currentTagId
                                && <PressableStyled
                                  style={{ position: 'absolute', top: 0, right: 0 }}
                                  onPress={() => {
                                    logEvent('AssetRow', 'delete_tag');
                                    showAlert(
                                      'Delete solve tag?',
                                      <Text>
                                        {'Do you want to delete this tag - '}
                                        <Text style={Style.alertBoldText}>
                                          {currentTagId}
                                        </Text>?
                                      </Text>,
                                      true,
                                      () => {
                                        logEvent('AssetRow', 'delete_tag_confirm');
                                        props.setLoading(true);
                                        deleteSolveTag(
                                          props.asset.id,
                                          currentTagId,
                                        )
                                          .then((response) => {
                                            props.setLoading(false);
                                            if (response && !response?.isError) {
                                              props.showAlert(
                                                'Success',
                                                DELETE_SOLVE_TAG_SUCCESS,
                                                false,
                                              );
                                              solveTags.splice(currentTagIndex, 1);
                                              if (currentTagIndex >= solveTags.length - 1) {
                                                setCurrentTag(currentTagIndex - 1);
                                              }
                                            } else deleteError(response?.errorText);
                                          }).catch((error) => {
                                            deleteError(error?.errorText);
                                            props.setLoading(false);
                                          });
                                      },
                                      'Delete',
                                    );
                                  }}
                                  testID='am-delete-asset-tag-btn'
                                >
                                  <DeleteIcon24 />
                                </PressableStyled>}
                            </View>
                            {tagsLoading && <View style={{
                              position: 'absolute', width: '100%', height: '100%', backgroundColor: '#00000040', justifyContent: 'center', alignItems: 'center',
                            }}>
                              <ActivityIndicator size={'large'} />
                            </View>
                            }
                          </View >
                        </View >
                        <View style={{ justifyContent: 'center' }}>
                          <PressableStyled
                            style={{
                              marginLeft: 12,
                              marginRight: 24,
                              backgroundColor: qrCodeSelected
                                ? Colors.light.hereworksBlue500
                                : '#fff',
                              borderRadius: 4,
                              borderColor: Colors.light.hereworksBlue500,
                              borderWidth: 1,
                            }}
                            onPress={() => {
                              if (!qrCodeSelected) {
                                logEvent('AssetRow', 'Set_QR_selected');
                                setQrCodeSelected(!qrCodeSelected);
                              }
                            }}
                          >
                            <Text
                              style={[
                                styles.secondaryButtonText,
                                { color: !qrCodeSelected ? Colors.light.hereworksBlue500 : '#fff' },
                              ]}
                            >
                              QR CODE
                            </Text>
                          </PressableStyled>
                          <PressableStyled
                            style={{
                              marginLeft: 12,
                              marginRight: 24,
                              marginTop: 8,
                              backgroundColor: !qrCodeSelected
                                ? Colors.light.hereworksBlue500
                                : '#fff',
                              borderRadius: 4,
                              borderColor: Colors.light.hereworksBlue500,
                              borderWidth: 1,
                            }}
                            onPress={() => {
                              if (qrCodeSelected) {
                                logEvent('AssetRow', 'Set_NFC_selected');
                                setQrCodeSelected(!qrCodeSelected);
                              }
                            }}
                          >
                            <Text
                              style={[
                                styles.secondaryButtonText,
                                { color: qrCodeSelected ? Colors.light.hereworksBlue500 : '#fff' },
                              ]}
                            >
                              NFC TAG
                            </Text>
                          </PressableStyled>
                        </View>
                      </View >
                      <View
                        style={{
                          borderColor: Colors.light.hereworksBlue200,
                          borderTopWidth: 1,
                          paddingHorizontal: 12,
                          paddingVertical: 16,
                          flexDirection: 'row',
                        }}
                      >
                        <View style={{ flex: 1 }}>
                          <Text style={styles.activatedText}>{'GENERATED QR LINK'}</Text>
                          <Text
                            numberOfLines={1}
                            selectable
                          >{`${`${domain}${currentTagId}`}?ss=${qrCodeSelected ? 'QR' : 'NFC'}`}</Text>
                        </View>
                        <Pressable
                          style={{
                            justifyContent: 'flex-end',
                          }}
                          onPress={() => {
                            logEvent('AssetRow', 'Copy_Asset_URL');
                            const url: string = `${`${domain}${currentTagId}`}?ss=${qrCodeSelected ? 'QR' : 'NFC'}`;
                            Clipboard.setStringAsync(url);
                            showAlert(COPIED_TO_CLIPBOARD, url, false, null, null);
                          }}
                          testID='am-copy-asset-link-btn'
                        >
                          {({ hovered }: PressableState): ReactElement => (
                            <View
                              style={{
                                borderColor: Colors.light.hereworksBlue100,
                                borderWidth: 1,
                                borderRadius: 5,
                                opacity: hovered ? 0.5 : 1,
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}
                            >
                              <CopyIcon />
                            </View>
                          )}
                        </Pressable>
                      </View>
                    </View >
                    {renderNotes()}
                  </View >
                </>
              )}
              {selectedTab === 0 && renderMainView()}
              {selectedTab === 0 && (customFields?.length > 0 && <>
                <View
                  testID='assets-add-info-modal'
                  style={styles.section}
                >
                  <Text style={styles.sectionTitle}>{'ADDITIONAL INFO'}</Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                    {customFields?.map((field: AssetCustomField, index: number) => {
                      const title = field.name;
                      let { value } = field;
                      if (field.format === ADDITIONAL_FIELD_TYPES.DATE) {
                        const date: Date = new Date(value);
                        if (date instanceof Date && !Number.isNaN(date.getTime())) {
                          value = Moment(date).format('DD/MM/YYYY');
                        }
                      }
                      if (field.format === ADDITIONAL_FIELD_TYPES.LINK && value?.length > 0) {
                        return <View style={styles.labelSetThirdSize} key={index}>
                          <Text selectable style={styles.mainDescText}>
                            {title?.trim()}
                          </Text>
                          <Pressable onPress={() => {
                            logEvent('AssetRow', 'open_url');
                            openInNewTab(value);
                          }}>
                            <Text style={styles.linkText} numberOfLines={1} ellipsizeMode='tail'>
                              {value}
                            </Text>
                          </Pressable>
                        </View>;
                      }
                      return (
                        <View style={styles.labelSetThirdSize} key={index}>
                          <Text selectable style={styles.mainDescText}>
                            {title?.trim()}
                          </Text>
                          <Text selectable style={styles.mainValueText}>
                            {value?.trim()}
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                </View>
              </>)}
            </View >
            <EditAssetModal
              asset={props.asset}
              visible={editAssetVisible}
              setModalVisible={setEditAssetVisible}
              {...{ image, updateImage, isAdmin }}
              customFields={loadCustomFields(props.template, props.asset, true)}
              updateAsset={props.updateAsset}
              deleteAsset={props.deleteAsset}
              showAlert={props.showAlert}
              templateType={props.template?.type}
            />
          </Animatable.View >
        ) : renderCardClosedView()}
        <PressableStyled
          style={styles.expandButton}
          onPress={() => {
            logEvent('AssetRow', 'open_close_card');
            setCardOpen(!cardOpen);
          }}
          testID='am-open-asset-details-btn'
        >
          <DownArrow24 style={cardOpen ? { transform: [{ rotateZ: '180deg' }] } : {}} />
        </PressableStyled>
        {canDeleteAsset && <PressableStyled
          style={{
            position: 'absolute',
            right: 20,
            top: 18,
          }}
          onPress={deleteAsset}
          testID='am-delete-asset-btn'
        >
          <DeleteIcon24 />
        </PressableStyled>}
      </View >
      {cardOpen ? (
        <Animatable.View
          animation={'fadeIn'}
          style={{
            backgroundColor: Colors.light.hereworksWhite100,
            borderRadius: 10,
            height: 78,
            marginTop: 24,
            alignSelf: 'stretch',
            alignItems: 'center',
            flexDirection: 'row',
            overflow: 'hidden',
          }}
        >
          <View style={{ marginLeft: 24, flex: 3 }}>
            <Text style={styles.raisedTitle}>{'Tickets raised against asset'}</Text>
            <PressableStyled
              onPress={() => {
                logEvent('AssetRow', 'View_log');
                dispatch(updateTicketsFilterAssetId(props.asset.id));
                dispatch(updateTicketsFilterAssetName(props.asset.name));
                let selectedPeriod = 'day';
                if (weekSelected) selectedPeriod = 'week';
                if (monthSelected) selectedPeriod = 'month';
                if (yearSelected) selectedPeriod = 'year';
                navigation.navigate(AppScreen.TICKETS as any, {
                  selectedTimePeriod: selectedPeriod,
                });
              }}
              style={{ flexDirection: 'row', alignItems: 'center' }}
            >
              <Text
                style={{
                  color: Colors.light.hereworksBlue500,
                  fontFamily: 'Roboto_400Regular',
                  fontSize: 14,
                  lineHeight: 22,
                }}
              >
                View log
              </Text>
              <OrionArrowIcon style={{ transform: [{ rotateZ: '180deg' }] }} />
            </PressableStyled>
          </View>
          <View
            style={{
              flex: 6,
              borderRadius: 10,
              backgroundColor: Colors.light.hereworksBlue500,
              height: 60,
              flexDirection: 'row',
              alignItems: 'flex-end',
            }}
          >
            <View style={{ flex: 1, flexDirection: 'row', alignItems: 'flex-end' }}>
              <Text style={styles.ticketsValue}>{ticketCounts[selectedFilterIndex]}</Text>
              <Text style={styles.ticketsTitle}>Tickets</Text>
            </View>
            <View
              style={{
                paddingHorizontal: 24,
                height: 60,
                backgroundColor: Colors.light.hereworksBlue400,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PressableStyled
                style={[
                  styles.dateButton,
                  {
                    backgroundColor: daySelected ? '#fff' : Colors.light.hereworksBlue400,
                  },
                ]}
                onPress={() => {
                  logEvent('AssetRow', 'DAY');
                  if (!daySelected) {
                    setDaySelected(true);
                    setSelectedFilterIndex(0);
                  }
                  setWeekSelected(false);
                  setMonthSelected(false);
                  setYearSelected(false);
                }}
              >
                <Text
                  style={[
                    styles.dateButtonTitle,
                    {
                      color: daySelected ? Colors.light.hereworksBlue500 : '#fff',
                    },
                  ]}
                >
                  DAY
                </Text>
              </PressableStyled>
              <PressableStyled
                style={[
                  styles.dateButton,
                  {
                    backgroundColor: weekSelected ? '#fff' : Colors.light.hereworksBlue400,
                  },
                ]}
                onPress={() => {
                  logEvent('AssetRow', 'WEEK');
                  if (!weekSelected) {
                    setWeekSelected(true);
                    setSelectedFilterIndex(1);
                  }
                  setDaySelected(false);
                  setMonthSelected(false);
                  setYearSelected(false);
                }}
              >
                <Text
                  style={[
                    styles.dateButtonTitle,
                    {
                      color: weekSelected ? Colors.light.hereworksBlue500 : '#fff',
                    },
                  ]}
                >
                  WEEK
                </Text>
              </PressableStyled>
              <PressableStyled
                style={[
                  styles.dateButton,
                  {
                    backgroundColor: monthSelected ? '#fff' : Colors.light.hereworksBlue400,
                  },
                ]}
                onPress={() => {
                  logEvent('AssetRow', 'MONTH');
                  if (!monthSelected) {
                    setMonthSelected(true);
                    setSelectedFilterIndex(2);
                  }
                  setDaySelected(false);
                  setWeekSelected(false);
                  setYearSelected(false);
                }}
              >
                <Text
                  style={[
                    styles.dateButtonTitle,
                    {
                      color: monthSelected ? Colors.light.hereworksBlue500 : '#fff',
                    },
                  ]}
                >
                  MONTH
                </Text>
              </PressableStyled>
              <PressableStyled
                style={[
                  styles.dateButton,
                  {
                    backgroundColor: yearSelected ? '#fff' : Colors.light.hereworksBlue400,
                  },
                ]}
                onPress={() => {
                  logEvent('AssetRow', 'YEAR');
                  if (!yearSelected) {
                    setYearSelected(true);
                    setSelectedFilterIndex(3);
                  }
                  setDaySelected(false);
                  setWeekSelected(false);
                  setMonthSelected(false);
                }}
              >
                <Text
                  style={[
                    styles.dateButtonTitle,
                    {
                      color: yearSelected ? Colors.light.hereworksBlue500 : '#fff',
                    },
                  ]}
                >
                  YEAR
                </Text>
              </PressableStyled>
            </View>
          </View>
        </Animatable.View>
      ) : null}
      <AlertModal
        modalVisible={alertVisible}
        error={alertError}
        title={alertTitle}
        action={alertAction}
        actionTitle={alertActionTitle}
        message={alertMessage}
        isMessage={alertIsMessage}
        setModalVisible={setAlertVisible}
      />
      <ReportTicketModal
        isVisible={reportIssueModalVisible}
        hide={() => setReportIssueModalVisible(false)}
        assetName={props.asset.name}
        template={props.template}
        solveTagId={(solveTags[currentTagIndex] ?? solveTags[0]).solveTagId
          ?? props.asset.id}
        refresh={loadTicketCount}
      />
    </Animated.View >
  );
}

const styles = StyleSheet.create({
  assetRowHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomColor: Colors.light.hereworksBlue100,
    borderBottomWidth: 1,
    height: 69,
    paddingHorizontal: 24,
  },
  mainInformationContainer: {
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue200,
    backgroundColor: 'white',
    borderRadius: 12,
    marginVertical: 16,
    marginHorizontal: 24,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 4,
    flexDirection: 'row',
  },
  section: {
    marginHorizontal: 24,
    marginBottom: 4,
  },
  sectionTitle: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    lineHeight: 14,
    color: Colors.light.hereworksBlack400,
    width: '100%',
    paddingBottom: 2,
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue200,
  },
  rowText: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 16,
    color: Colors.light.hereworksPrimaryGrey,
    textAlign: 'left',
    marginLeft: 30,
  },
  activatedText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 11,
    lineHeight: 13,
    marginBottom: 4,
    marginRight: 8,
    color: Colors.light.hereworksBlack400,
  },
  tagSection: {
    marginVertical: 12,
    marginHorizontal: 12,
    flex: 1,
    flexDirection: 'row',
  },
  tagNumbers: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 20,
    color: Colors.light.hereworksBlack600,
    textAlign: 'center',
    flex: 1,
  },
  labelsContainer: {
    marginLeft: 16,
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  labelSetQuarterSize: {
    flexBasis: '25%',
  },
  labelSetThirdSize: {
    flexBasis: '33%',
  },
  labelSetHalfSize: {
    flexBasis: '50%',
  },
  mainDescText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 14,
    marginBottom: 8,
    color: Colors.light.hereworksBlack400,
  },
  noteDateText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 14,
    color: Colors.light.hereworksBlack400,
  },
  noteNameText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 11,
    lineHeight: 17,
    color: Colors.light.hereworksBlack400,
    marginTop: 2,
  },
  noteTitleText: {
    flex: 1,
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    lineHeight: 14,
    color: Colors.light.hereworksBlack600,
  },
  buttonText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 22,
    marginVertical: 5,
    marginHorizontal: 12,
    color: '#fff',
  },
  secondaryButton: {
    borderRadius: 4,
    borderColor: Colors.light.hereworksBlue500,
    borderWidth: 1,
    zIndex: 2,
    marginEnd: 8,
  },
  secondaryButtonText: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    lineHeight: 20,
    height: 20,
    marginVertical: 4,
    marginHorizontal: 12,
  },
  assetTitleSub: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 12,
    lineHeight: 18,
    marginTop: 12,
    color: Colors.light.hereworksBlack400,
  },
  assetTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 18,
    lineHeight: 27,
    marginBottom: 12,
    color: Colors.light.hereworksBlack600,
  },
  raisedTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 18,
    lineHeight: 27,
    color: Colors.light.hereworksBlack600,
  },
  mainValueText: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    lineHeight: 16,
    color: Colors.light.hereworksBlack600,
    marginBottom: 12,
  },
  viewMapText: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    lineHeight: 16,
    color: Colors.light.hereworksBlue500,
    textDecorationColor: Colors.light.hereworksBlue500,
    textDecorationLine: 'underline',
    padding: 8,
  },
  linkText: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    lineHeight: 16,
    color: Colors.light.hereworksBlue500,
    textDecorationLine: 'underline',
    marginBottom: 12,
  },
  ticketsValue: {
    fontSize: 34,
    lineHeight: 42,
    marginBottom: 9,
    fontFamily: 'Poppins_600SemiBold',
    color: '#fff',
    marginLeft: 24,
  },
  ticketsTitle: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: '#fff',
    marginLeft: 4,
    marginBottom: 14,
  },
  dateButtonTitle: {
    marginVertical: 4,
    marginHorizontal: 12,
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    lineHeight: 20,
  },
  dateButton: {
    borderRadius: 4,
    marginHorizontal: 4,
    borderWidth: 1,
    borderColor: '#fff',
  },
  detailedFilterButton: {
    backgroundColor: '#fff',
    padding: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    marginEnd: 12,
  },
  assetHeaderButtonSectionDivider: {
    width: 1,
    marginEnd: 12,
  },
  assetHeaderButtonSection: {
    height: 28,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
  },
  addNoteButton: {
    marginVertical: 16,
    backgroundColor: '#fff',
    borderRadius: 4,
    borderColor: Colors.light.hereworksBlue500,
    borderWidth: 1,
    alignSelf: 'flex-start',
  },
  notesSection: {
    borderColor: Colors.light.hereworksBlue200,
    borderLeftWidth: 1,
    paddingHorizontal: 24,
    flex: 3,
  },
  expandButton: {
    position: 'absolute',
    right: 50,
    top: 16,
  },
});
